import { FirebaseApp } from "firebase/app";
import {
  getFirestore,
  onSnapshot,
  collection,
  doc,
  setDoc,
} from "firebase/firestore";
import {
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  ReactElement,
} from "react";
import { Release } from "../schemas/release";
import { releaseConverter } from "../schemas/converter";
import { DisplayMode } from "../schemas/types";
import {
  getGithubLink,
  getCritiqueLink,
  compareVersions,
  formatDateUTC,
} from "../util";
import { ExtendedUser } from "./app";
import { StagingReleasesTable } from "./staging-releases-table";

export function Releases({
  app,
  user,
}: {
  app: FirebaseApp;
  user?: ExtendedUser | null;
}) {
  const [releases, setReleases] = useState<Release[]>([]);

  useEffect(() => {
    const firestore = getFirestore(app);
    const unsub = onSnapshot(
      collection(firestore, "releases").withConverter(releaseConverter),
      (snap) => {
        const releasesData: Release[] = [];
        snap.docs.forEach((docSnap) => {
          releasesData.push(docSnap.data());
        });
        setReleases(releasesData);
      }
    );
    return unsub;
  }, [app]);

  return (
    <div className="releases-container">
      <ReleasesTable app={app} releases={releases} user={user} />
    </div>
  );
}

function InputField({
  mode,
  fieldName,
  formatter,
  releaseValues,
  setReleaseValues,
}: {
  mode: DisplayMode;
  fieldName: keyof Omit<
    Release,
    "stagingReleases" | "mergedToMaster" | "importedToGoogle3"
  >;
  formatter: (val: string) => ReactElement;
  releaseValues: Release;
  setReleaseValues: Dispatch<SetStateAction<Release>>;
}) {
  if (mode === DisplayMode.DISPLAY) {
    return (
      <span>
        {releaseValues[fieldName] && formatter(releaseValues[fieldName]!)}
      </span>
    );
  }
  return (
    <input
      type="text"
      onChange={(e) =>
        setReleaseValues({
          ...releaseValues,
          [fieldName]: e.target.value,
        })
      }
      value={releaseValues[fieldName] || ""}
    />
  );
}

function CheckField({
  mode,
  fieldName,
  releaseValues,
  setReleaseValues,
}: {
  mode: "display" | "edit";
  fieldName: "mergedToMaster" | "importedToGoogle3";
  releaseValues: Release;
  setReleaseValues: Dispatch<SetStateAction<Release>>;
}) {
  if (mode === "display") {
    return <span>{releaseValues[fieldName] ? "yes" : "no"}</span>;
  }
  return (
    <input
      type="checkbox"
      onChange={(e) =>
        setReleaseValues({
          ...releaseValues,
          [fieldName]: e.target.checked,
        })
      }
      checked={releaseValues[fieldName]}
    />
  );
}

function ReleaseRow({
  app,
  isAdmin,
  release,
}: {
  app: FirebaseApp;
  isAdmin?: boolean;
  release: Release;
}) {
  const [mode, setMode] = useState<DisplayMode>(DisplayMode.DISPLAY);
  const [releaseValues, setReleaseValues] = useState(release);
  const [noteVisible, setNoteVisible] = useState(false);
  const hasNote = !!releaseValues.noteText;
  let rowStatus = releaseValues.releaseDate ? "complete" : "";
  if (
    !rowStatus &&
    new Date().valueOf() - new Date(releaseValues.weekStartDate).valueOf() <
      1000 * 60 * 60 * 24 * 7
  ) {
    rowStatus = "current";
  }

  useEffect(() => {
    // Render any new values from incoming Firestore realtime snapshots
    // unless we are in edit mode.
    if (mode === DisplayMode.DISPLAY) {
      setReleaseValues(release);
    }
  }, [mode, release]);

  async function save() {
    console.log(JSON.stringify(releaseValues));
    const firestore = getFirestore(app);
    const releaseRef = doc(
      firestore,
      `releases/${release.version}`
    ).withConverter(releaseConverter);
    await setDoc(releaseRef, releaseValues);
    setMode(DisplayMode.DISPLAY);
  }

  const editButton =
    mode === DisplayMode.DISPLAY ? (
      <button onClick={() => setMode(DisplayMode.EDIT)}>edit</button>
    ) : (
      <>
        <button onClick={() => setMode(DisplayMode.DISPLAY)}>x</button>
        <button className="ok" onClick={save}>
          save
        </button>
      </>
    );

  const notesEl = releaseValues.noteText && (
    <tr>
      {isAdmin && <td></td>}
      <td colSpan={11}>{releaseValues.noteText}</td>
    </tr>
  );

  const notesEditEl = (
    <tr>
      {isAdmin && <td></td>}
      <td colSpan={11}>
        <span>Notes:</span>
        <input
          type="text"
          placeholder="enter notes here"
          value={releaseValues.noteText || ""}
          onChange={(e) =>
            setReleaseValues({
              ...releaseValues,
              noteText: e.target.value,
            })
          }
        />
      </td>
    </tr>
  );
  return (
    <>
      <tr className={rowStatus} key={release.version}>
        {isAdmin && <td>{editButton}</td>}
        <td>{release.version}</td>
        <td>
          <InputField
            mode={mode}
            fieldName="weekStartDate"
            formatter={(val: string) => (
              <span>{formatDateUTC(new Date(val))}</span>
            )}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td>
          <InputField
            mode={mode}
            fieldName="githubPR"
            formatter={getGithubLink}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td>
          {release.stagingReleases?.length && (
            <StagingReleasesTable
              mode={mode}
              releaseValues={releaseValues}
              setReleaseValues={setReleaseValues}
            />
          )}
        </td>
        <td>
          <InputField
            mode={mode}
            fieldName="releaseDate"
            formatter={(val: string) => (
              <span>{formatDateUTC(new Date(val))}</span>
            )}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td>
          <InputField
            mode={mode}
            fieldName="releaseDateCDN"
            formatter={(val: string) => (
              <span>{formatDateUTC(new Date(val))}</span>
            )}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td className={releaseValues.mergedToMaster ? "" : "incomplete"}>
          <CheckField
            mode={mode}
            fieldName="mergedToMaster"
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td className={releaseValues.releaseNotesCL ? "" : "incomplete"}>
          <InputField
            mode={mode}
            fieldName="releaseNotesCL"
            formatter={(val: string) => {
              if (val != null && val !== "NA") {
                return getCritiqueLink(val);
              } else {
                return <span>{val || ""}</span>;
              }
            }}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td
          className={
            releaseValues.refDocsCL
              ? releaseValues.refDocsCL === "NA"
                ? "na"
                : releaseValues.refDocsCL === "need docs"
                ? "need-docs"
                : ""
              : "incomplete"
          }
        >
          <InputField
            mode={mode}
            fieldName="refDocsCL"
            formatter={(val: string) => {
              if (val != null && val !== "NA" && val !== "need docs") {
                return getCritiqueLink(val);
              } else {
                return <span>{val || ""}</span>;
              }
            }}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td className={releaseValues.importedToGoogle3 ? "" : "incomplete"}>
            <CheckField
              mode={mode}
              fieldName="importedToGoogle3"
              releaseValues={releaseValues}
              setReleaseValues={setReleaseValues}
            />
        </td>
        <td>
          <InputField
            mode={mode}
            fieldName="operator"
            formatter={(val: string) => <span>{val}</span>}
            releaseValues={releaseValues}
            setReleaseValues={setReleaseValues}
          />
        </td>
        <td>
          {mode === DisplayMode.DISPLAY && hasNote && (
            <button
              className="other"
              onClick={() => setNoteVisible(!noteVisible)}
            >
              {noteVisible ? "hide" : "show"}
            </button>
          )}
        </td>
      </tr>
      {mode === DisplayMode.DISPLAY && noteVisible && notesEl}
      {mode === DisplayMode.EDIT && notesEditEl}
      {rowStatus === "current" && (
        <tr>
          <td className="current-divider" colSpan={100}></td>
        </tr>
      )}
    </>
  );
}

export function ReleasesTable({
  app,
  user,
  releases,
}: {
  app: FirebaseApp;
  user?: ExtendedUser | null;
  releases: Release[];
}) {
  const releasesSorted = releases.slice();
  releasesSorted.sort((relA, relB) => {
    const dateA = new Date(relA.weekStartDate);
    const dateB = new Date(relB.weekStartDate);
    if (dateA.valueOf() < dateB.valueOf()) {
      return 1;
    } else if (dateA.valueOf() > dateB.valueOf()) {
      return -1;
    }
    return compareVersions(relA.version, relB.version);
  });
  const rows = releasesSorted.map((release) => (
    <ReleaseRow
      key={release.version}
      app={app}
      release={release}
      isAdmin={user?.isAdmin}
    />
  ));
  return (
    <table className="releases-table">
      <thead>
        <tr>
          {user?.isAdmin && <td style={{ width: "50px" }}></td>}
          <td style={{ width: "60px" }}>version</td>
          <td style={{ width: "80px" }}>week starting</td>
          <td style={{ width: "60px" }}>Github PR</td>
          <td style={{ width: "350px" }}>Staging Releases</td>
          <td style={{ width: "80px" }}>Prod Release NPM</td>
          <td style={{ width: "80px" }}>Prod Release CDN</td>
          <td style={{ width: "70px" }}>Merged to master</td>
          <td style={{ width: "110px" }}>Release Notes CL</td>
          <td style={{ width: "110px" }}>Reference Docs CL</td>
          <td style={{ width: "70px" }}>Imported to Google3</td>
          <td style={{ width: "80px" }}>Operator</td>
          <td style={{ width: "50px" }}>Notes</td>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  );
}
